import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, Wallet, ShieldCheck, ArrowLeftRight, FolderLock,
  Store, Users, PlusCircle, ScanLine, LogOut, Menu, X, Camera,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "../lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

const NAV = {
  retailer: [
    { to: "/retailer", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/retailer/issue", label: "Issue Receipt", icon: PlusCircle },
    { to: "/retailer/receipts", label: "Issued Receipts", icon: Receipt },
  ],
  customer: [
    { to: "/wallet", label: "My Wallet", icon: Wallet, end: true },
    { to: "/wallet/add", label: "Add Receipt", icon: Camera },
    { to: "/claim", label: "Claim Receipt", icon: ScanLine },
    { to: "/warranties", label: "Warranties", icon: ShieldCheck },
    { to: "/transfers", label: "Transfers", icon: ArrowLeftRight },
    { to: "/proof-packs", label: "Proof Packs", icon: FolderLock },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/stores", label: "Stores", icon: Store },
    { to: "/admin/users", label: "Users & Roles", icon: Users },
  ],
};

function NavItem({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
        active ? "bg-brand text-white shadow-[0_8px_24px_-10px_rgba(92,36,255,0.8)]"
               : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      )}
    >
      <Icon className="h-[18px] w-[18px]" />
      {item.label}
    </Link>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV[user?.role] || NAV.customer;

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const doLogout = async () => { await logout(); navigate("/"); };

  const Sidebar = ({ mobile }) => (
    <aside className={cn(
      "flex flex-col gap-1 border-r border-border bg-card",
      mobile ? "w-72 p-5" : "hidden lg:flex w-64 p-5 h-screen sticky top-0"
    )}>
      <Link to="/" className="mb-6 px-1"><Logo /></Link>
      <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {user?.role} area
      </p>
      <nav className="flex flex-col gap-1">
        {items.map((it) => (
          <NavItem key={it.to} item={it} active={isActive(it)} onClick={() => setOpen(false)} />
        ))}
      </nav>
      <div className="mt-auto pt-4">
        <button onClick={doLogout} data-testid="sidebar-logout"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full"><Sidebar mobile /></div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-border bg-card/70 px-4 py-3 backdrop-blur-xl lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
          <div className="lg:hidden"><Logo withText={false} /></div>
          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 transition-colors hover:bg-accent" data-testid="user-menu">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-brand text-xs text-white">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm font-semibold sm:block">{user?.name || user?.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
